// Üzenet-űrlapok tiszta segédei (nem-komponens): form-értékek, típus↔címzés-kind térkép, plain-text↔HTML
// konverzió, idő-konverzió (helyi ↔ UTC ISO — a meetings mintája), a wire-body építők és a lokalizált
// kézi resolverek. Külön fájl a komponensektől (react-refresh tiszta marad) — a minta a features/assets/form.ts.
//
// RICH-TEXT: a törzs RICH-TEXT szerkesztőben (TipTap) készül — mint a legacy WYSIWYG-je. A form
// értéke ezért MINDIG HTML, és a korábbi „plain szöveg ↔ minimális HTML" kettősség (`bodyIsHtml` mód) MEGSZŰNT:
// a szerkesztés a tárolt HTML-t nyitja meg, a mentés a szerkesztő HTML-jét küldi. A hossz-szabály (min. 25) a
// LÁTHATÓ szöveg hosszán fut (a `plainTextLength` tag-strippel), tehát a markup nem tudja becsalni.
import { type MessageComposeBodySchema, MessageContentSchema } from '@atlas/contracts';
import { type TFunction } from 'i18next';
import { type FieldError, type Resolver } from 'react-hook-form';
import { type z } from 'zod';

import { richTextToPlainText } from '@/lib/sanitize-html';

/** A compose-body content-részének wire-típusa (subject/body/3 link). */
export type MessageContentBody = z.infer<typeof MessageContentSchema>;

// ---------------------------------------------------------------------------
// Címzés: típus ↔ kind térkép (a contract discriminated unionja 1:1)
// ---------------------------------------------------------------------------

export const INTERNAL_KINDS = ['everyone', 'departments', 'teams', 'colleagues'] as const;
export const EXTERNAL_KINDS = ['all_clients', 'accounts', 'contacts', 'services'] as const;
export type MessageType = 'internal' | 'external';
export type AudienceKind = (typeof INTERNAL_KINDS)[number] | (typeof EXTERNAL_KINDS)[number];

/** A típushoz tartozó címzés-kind opciók (kölcsönösen kizáró — típus-váltás kind-resetet kényszerít). */
export function kindsForType(type: MessageType): readonly AudienceKind[] {
  return type === 'internal' ? INTERNAL_KINDS : EXTERNAL_KINDS;
}

/**
 * A címzés-fajta az űrlapon: egy kind, vagy MÉG NINCS VÁLASZTVA (`''`). Előválasztás SZÁNDÉKOSAN nincs (kolléga-
 * észrevétel): egy alapból kijelölt „Mindenki" mellett egy elfelejtett átállítás az egész cégnek küldött.
 */
export type AudienceChoice = AudienceKind | '';

/** A széles („mindenkinek szóló") kind — küldés előtt megerősítést kér. */
export function isBroadAudience(kind: AudienceChoice): kind is 'everyone' | 'all_clients' {
  return kind === 'everyone' || kind === 'all_clients';
}

/** Igaz, ha a kindhez célzott id-lista kell (a flag-audience-eknek és a még választatlannak nincs). */
export function kindNeedsIds(kind: AudienceChoice): kind is AudienceKind {
  return kind !== '' && !isBroadAudience(kind);
}

/** A kindhez tartozó refs-lista kulcsa (a /messages/refs válaszában); flag-kindnél null. */
export function refsKeyForKind(
  kind: AudienceChoice,
): 'employees' | 'departments' | 'teams' | 'accounts' | 'contacts' | 'services' | null {
  switch (kind) {
    case 'departments':
      return 'departments';
    case 'teams':
      return 'teams';
    case 'colleagues':
      return 'employees';
    case 'accounts':
      return 'accounts';
    case 'contacts':
      return 'contacts';
    case 'services':
      return 'services';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// A törzs LÁTHATÓ hossza (a rich-text HTML-ből)
// ---------------------------------------------------------------------------

/** HTML → plain-text hossz (tag-strip + entitás-lazítás + whitespace-collapse) — a BFF min. 25 szabályának
 *  kliens-oldali elő-ellenőrzése (a markup-padding, pl. `<p></p>`-halmozás így NEM számít bele). */
export function plainTextLength(html: string): number {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim().length;
}

// ---------------------------------------------------------------------------
// Idő: helyi datetime-local ↔ UTC ISO (a meeting-form-sheet mintája)
// ---------------------------------------------------------------------------

/** ISO (UTC) → 'YYYY-MM-DDTHH:mm' HELYI idő (a datetime-local inputhoz). */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 'YYYY-MM-DDTHH:mm' (helyi) → ISO (UTC). Üres/érvénytelen → ''. */
export function localInputToIso(local: string): string {
  if (!local) return '';
  const d = new Date(local); // helyi időként értelmezve
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

// ---------------------------------------------------------------------------
// Tartalom-mezők (compose + edit KÖZÖS 5 mezője)
// ---------------------------------------------------------------------------

/** A tartalom-mezők form-értékei. A `body` MINDIG HTML (a rich-text szerkesztő kimenete). */
export interface MessageContentValues {
  subject: string;
  body: string;
  googleDriveLink: string;
  googleMeetLink: string;
  zoomLink: string;
}

export const EMPTY_MESSAGE_CONTENT: MessageContentValues = {
  subject: '',
  body: '',
  googleDriveLink: '',
  googleMeetLink: '',
  zoomLink: '',
};

/** Tartalom form-értékek → wire-content ('' → null a linkeken; a törzs HTML-ként). */
export function toContentBody(v: MessageContentValues): MessageContentBody {
  const emptyToNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  return {
    subject: v.subject.trim(),
    // A törzs a szerkesztő HTML-je; üres szerkesztő → üres string (a validáció fogja meg, nem itt csonkítunk).
    body: v.body.trim(),
    googleDriveLink: emptyToNull(v.googleDriveLink),
    googleMeetLink: emptyToNull(v.googleMeetLink),
    zoomLink: emptyToNull(v.zoomLink),
  };
}

/** A BFF plain-text minimum szabálya (messages.md: tag-strip + whitespace-collapse után min. 25). */
export const BODY_MIN_PLAIN = 25;

type FieldErrors = Record<string, FieldError>;

/**
 * A tartalom-mezők lokalizált validálása (a contract MessageContentSchema + a BFF plain-text szabály
 * kliens-oldali elő-futtatása — a szerver 422-je a fal). A hiba-kulcsok 1:1 a form-mezők nevei.
 */
function validateContent(v: MessageContentValues, t: TFunction): FieldErrors {
  const errors: FieldErrors = {};
  const wire = toContentBody(v);

  if (wire.subject === '') {
    errors.subject = { type: 'required', message: t('validation:messages.subject.required') };
  } else if (wire.subject.length > 255) {
    errors.subject = { type: 'max', message: t('validation:messages.subject.max') };
  }

  const plain = plainTextLength(wire.body);
  if (plain === 0) {
    errors.body = { type: 'required', message: t('validation:messages.body.required') };
  } else if (plain < BODY_MIN_PLAIN) {
    errors.body = {
      type: 'min',
      message: t('validation:messages.body.min', { count: BODY_MIN_PLAIN }),
    };
  } else if (wire.body.length > 100_000) {
    errors.body = { type: 'max', message: t('validation:messages.body.max') };
  }

  // A link-formátum forrás-igazsága a contract (http(s) URL + max 2083) — a safeParse a link-pathokra fut.
  const parsed = MessageContentSchema.safeParse(wire);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const field = issue.path[0];
      if (
        (field === 'googleDriveLink' || field === 'googleMeetLink' || field === 'zoomLink') &&
        !(field in errors)
      ) {
        errors[field] = { type: 'validate', message: t('validation:messages.link.url') };
      }
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Compose-form (típus + címzés + tartalom + küldés-mód)
// ---------------------------------------------------------------------------

export interface MessageComposeValues extends MessageContentValues {
  type: MessageType;
  /** A címzés-fajta — ÜRESEN indul (nincs előválasztott „Mindenki"), a választás kötelező. */
  audienceKind: AudienceChoice;
  /** A kiválasztott kind célzott id-listája (kind-váltás reseteli). */
  targetIds: string[];
  deliveryMode: 'send' | 'schedule';
  /** 'YYYY-MM-DDTHH:mm' HELYI idő (datetime-local) — a wire-re localInputToIso konvertál. */
  scheduledAtLocal: string;
}

export const EMPTY_MESSAGE_COMPOSE: MessageComposeValues = {
  ...EMPTY_MESSAGE_CONTENT,
  type: 'internal',
  audienceKind: '',
  targetIds: [],
  deliveryMode: 'send',
  scheduledAtLocal: '',
};

/**
 * ÜRES-e a megkezdett üzenet (a vázlat-mentéshez: üres űrlapról nincs vázlat). Csak a GÉPELT tartalom számít —
 * tárgy, látható törzs-szöveg, linkek, kiválasztott címzettek —, a választók alapértéke (típus, címzés-fajta) nem.
 */
export function isComposeEmpty(v: Partial<MessageComposeValues>): boolean {
  const filled = (s: string | undefined): boolean => (s ?? '').trim() !== '';
  return (
    !filled(v.subject) &&
    richTextToPlainText(v.body ?? '') === '' &&
    !filled(v.googleDriveLink) &&
    !filled(v.googleMeetLink) &&
    !filled(v.zoomLink) &&
    (v.targetIds ?? []).length === 0
  );
}

/** A compose wire-body-ja — a contract MessageComposeBodySchema inferált (korrelált union-)típusa. */
export type MessageComposeBody = z.infer<typeof MessageComposeBodySchema>;

/** Compose form-értékek → wire-body (flag-kindnél NINCS ids; schedule-nál UTC ISO scheduledAt).
 *  A type↔audience korrelációt ágankénti narrowing adja (a contract discriminated unionja a forrás-igazság). */
export function toComposeBody(v: MessageComposeValues): MessageComposeBody {
  const base = {
    ...toContentBody(v),
    delivery:
      v.deliveryMode === 'schedule'
        ? ({ mode: 'schedule', scheduledAt: localInputToIso(v.scheduledAtLocal) } as const)
        : ({ mode: 'send' } as const),
  };
  // A „mindenki"/„minden ügyfél" CSAK kifejezett választásból lesz: a választatlan (vagy a típushoz nem illő) kind
  // ÜRES célzott listát kap, amit a szerver 422-vel elutasít (fail-closed — sosem esik vissza „mindenkire").
  if (v.type === 'internal') {
    const kind = v.audienceKind;
    const audience =
      kind === 'everyone'
        ? { kind: 'everyone' as const }
        : kind === 'departments' || kind === 'teams' || kind === 'colleagues'
          ? { kind, ids: v.targetIds }
          : { kind: 'colleagues' as const, ids: [] };
    return { ...base, type: 'internal', audience };
  }
  const kind = v.audienceKind;
  const audience =
    kind === 'all_clients'
      ? { kind: 'all_clients' as const }
      : kind === 'accounts' || kind === 'contacts' || kind === 'services'
        ? { kind, ids: v.targetIds }
        : { kind: 'contacts' as const, ids: [] };
  return { ...base, type: 'external', audience };
}

/**
 * Kézi (nem-zodResolver) compose-resolver: a contract compose-sémája intersection + discriminated union —
 * a hiba-pathjai (pl. `audience.ids`) nem képződnének a form-mezőkre, ezért a szabályokat mezőnként,
 * lokalizált üzenettel futtatjuk. Az ÜRES célzott lista kliens-oldali hiba (a szerver 422-je a fal);
 * a schedule-mód jövőbeliséget követel (múltbeli → a BFF is 422).
 */
export function buildMessageComposeResolver(t: TFunction): Resolver<MessageComposeValues> {
  return (values) => {
    const errors: FieldErrors = { ...validateContent(values, t) };

    if (values.audienceKind === '') {
      errors.audienceKind = {
        type: 'required',
        message: t('validation:messages.audience.required'),
      };
    } else if (kindNeedsIds(values.audienceKind) && values.targetIds.length === 0) {
      errors.targetIds = { type: 'required', message: t('validation:messages.targets.required') };
    }

    if (values.deliveryMode === 'schedule') {
      const iso = localInputToIso(values.scheduledAtLocal);
      if (iso === '') {
        errors.scheduledAtLocal = {
          type: 'required',
          message: t('validation:messages.scheduledAt.required'),
        };
      } else if (iso <= new Date().toISOString()) {
        errors.scheduledAtLocal = {
          type: 'validate',
          message: t('validation:messages.scheduledAt.future'),
        };
      }
    }

    return Object.keys(errors).length > 0 ? { values: {}, errors } : { values, errors: {} };
  };
}

/** Kézi edit-resolver — CSAK az 5 tartalom-mező (címzett/típus/időzítés utólag nem szerkeszthető itt). */
export function buildMessageContentResolver(t: TFunction): Resolver<MessageContentValues> {
  return (values) => {
    const errors = validateContent(values, t);
    return Object.keys(errors).length > 0 ? { values: {}, errors } : { values, errors: {} };
  };
}
